import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { type LucideIcon } from "lucide-react";

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  colorScheme?: "blue" | "green" | "amber" | "purple" | "red";
  isLoading?: boolean;
  onClick?: () => void;
  isActive?: boolean;
}

const colorClasses = {
  blue: {
    border: "border-l-blue-500",
    activeBorder: "border-l-blue-600",
    gradient: "bg-gradient-to-br from-blue-50/30 to-transparent dark:from-blue-950/20",
    activeTint: "bg-blue-50/60 dark:bg-blue-950/30",
    icon: "text-blue-600 dark:text-blue-400",
  },
  green: {
    border: "border-l-green-500",
    activeBorder: "border-l-green-600",
    gradient: "bg-gradient-to-br from-green-50/30 to-transparent dark:from-green-950/20",
    activeTint: "bg-green-50/60 dark:bg-green-950/30",
    icon: "text-green-600 dark:text-green-400",
  },
  amber: {
    border: "border-l-amber-500",
    activeBorder: "border-l-amber-600",
    gradient: "bg-gradient-to-br from-amber-50/30 to-transparent dark:from-amber-950/20",
    activeTint: "bg-amber-50/60 dark:bg-amber-950/30",
    icon: "text-amber-600 dark:text-amber-400",
  },
  purple: {
    border: "border-l-purple-500",
    activeBorder: "border-l-purple-600",
    gradient: "bg-gradient-to-br from-purple-50/30 to-transparent dark:from-purple-950/20",
    activeTint: "bg-purple-50/60 dark:bg-purple-950/30",
    icon: "text-purple-600 dark:text-purple-400",
  },
  red: {
    border: "border-l-red-500",
    activeBorder: "border-l-red-600",
    gradient: "bg-gradient-to-br from-red-50/30 to-transparent dark:from-red-950/20",
    activeTint: "bg-red-50/60 dark:bg-red-950/30",
    icon: "text-red-600 dark:text-red-400",
  },
};

export function KPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  colorScheme = "blue",
  isLoading = false,
  onClick,
  isActive = false,
}: Readonly<KPICardProps>) {
  const colors = colorClasses[colorScheme];

  if (isLoading) {
    return (
      <Card className={cn("border-l-4", colors.border, colors.gradient)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <Icon className={cn("h-4 w-4", colors.icon)} />
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-8 w-24 animate-pulse rounded bg-muted" />
          {subtitle && <div className="mt-1 h-4 w-32 animate-pulse rounded bg-muted" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        "transition-all duration-150",
        isActive ? "border-l-[6px]" : "border-l-4",
        isActive ? colors.activeBorder : colors.border,
        isActive ? colors.activeTint : colors.gradient,
        onClick && "cursor-pointer hover:shadow-md hover:scale-[1.02]",
        isActive && "shadow-md scale-[1.02]",
      )}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={cn("h-4 w-4", colors.icon)} />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        {trend && (
          <p
            className={cn(
              "mt-1 text-xs font-medium",
              trend.isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
            )}
          >
            {trend.value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
